import { ColumnDef } from '@tanstack/react-table';
import {
  Plus,
  Check,
  ChevronRight,
  Coins,
  UserPlus,
  AlertTriangle,
  Search,
  Link2,
  MoreHorizontal,
  RefreshCw,
  TrendingUp,
  X,
} from 'lucide-react';
import { formatDateTime } from '@/lib/datetime';
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import type {
  AdPlatform,
  AdProvider,
  AdsAccount,
  AppPackageVO,
  OperatorUser,
} from '@/lib/api/types';
import { ConsumeDialog } from './consume-dialog';
import { BindingDialog } from './binding-dialog';
import { EmptyState } from '@/components/layout/empty-state';
import { DataTable } from '@/components/composite/data-table/data-table';
import { StatusBadge } from '@/components/composite/status-badge/status-badge';
import { SearchInput, CopyButton, DateRangePicker, FilterToolbar } from '@/components/composite';
import { getAvatarColor } from '@/lib/utils/avatar';
import { MAX_BATCH_SIZE } from '@/lib/api/accounts';
import type { BatchStatusAction } from './batch-status-dialog';
import { MoneyCell } from '@/components/composite/money-cell/money-cell';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils';

export interface AccountFilters {
  search?: string;
  status?: '0' | '1' | '2';
  platformId?: number;
  providerId?: number;
  accountName?: string;
  operatorId?: number;
  pkg?: string;
  createTimeStart?: string;
  createTimeEnd?: string;
  updateTimeStart?: string;
  updateTimeEnd?: string;
}

export interface AccountListViewProps {
  data: AdsAccount[] | undefined;
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  error: Error | null;
  filters: AccountFilters;
  canRecharge?: boolean;
  canAssign?: boolean;
  canReportConsume?: boolean;
  canManageBinding?: boolean;
  canBan?: boolean;
  canZero?: boolean;
  /** 当前批量模式：null 时不显示勾选列，由顶部「批量封禁/清零」按钮进入 */
  batchMode?: BatchStatusAction | null;
  /** 跨页累计的选中账户；行快照同时用于批量弹窗的状态预检 */
  selected?: Map<string, AdsAccount>;
  operators?: OperatorUser[];
  platforms?: AdPlatform[];
  providers?: AdProvider[];
  apps?: AppPackageVO[];
  onFilterChange: (f: AccountFilters) => void;
  onPageChange: (p: number) => void;
  onCreateAccount?: () => void;
  onBatchCreate?: () => void;
  onViewDetail: (accountId: string) => void;
  onRecharge?: (accountId: string, amount: string, requestId: string) => void;
  onAssign?: (accountId: string, operatorId: number) => void;
  onClearAssign?: (accountId: string) => void;
  onToggleSelect?: (account: AdsAccount, checked: boolean) => void;
  onTogglePage?: (accounts: AdsAccount[], checked: boolean) => void;
  /** 进入某个批量模式（点顶部按钮） */
  onStartBatch?: (action: BatchStatusAction) => void;
  /** 退出批量模式并清空选中 */
  onCancelBatch?: () => void;
  /** 打开确认弹窗，提交当前选中 */
  onConfirmBatch?: () => void;
  onRetry?: () => void;
}

export function AccountListView({
  data,
  total,
  page,
  pageSize,
  isLoading,
  error,
  filters,
  canRecharge,
  canAssign,
  canReportConsume,
  canManageBinding,
  canBan,
  canZero,
  batchMode,
  selected,
  operators = [],
  platforms = [],
  providers = [],
  apps = [],
  onFilterChange,
  onPageChange,
  onCreateAccount,
  onBatchCreate,
  onViewDetail,
  onRecharge,
  onAssign,
  onClearAssign,
  onToggleSelect,
  onTogglePage,
  onStartBatch,
  onCancelBatch,
  onConfirmBatch,
  onRetry,
}: AccountListViewProps) {
  const { toast } = useToast();

  // 高频:充值 popover(同一时间仅一个)
  const [openRechargeId, setOpenRechargeId] = React.useState<string | null>(null);
  const [rechargeAmount, setRechargeAmount] = React.useState('');

  // 低频操作:行尾 ⋯ 菜单(同一时间仅一个)
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

  const [consumeDialogAccount, setConsumeDialogAccount] = React.useState<AdsAccount | null>(null);
  const [bindingDialogAccount, setBindingDialogAccount] = React.useState<AdsAccount | null>(null);
  const [assignDialogAccount, setAssignDialogAccount] = React.useState<AdsAccount | null>(null);

  // 次要筛选生效计数(折叠交互由 FilterToolbar 接管)
  const moreFilterCount =
    (filters.accountName ? 1 : 0) +
    (filters.pkg ? 1 : 0) +
    (filters.platformId ? 1 : 0) +
    (filters.providerId ? 1 : 0) +
    (filters.operatorId ? 1 : 0) +
    (filters.createTimeStart || filters.createTimeEnd ? 1 : 0) +
    (filters.updateTimeStart || filters.updateTimeEnd ? 1 : 0);

  const closeRecharge = () => {
    setOpenRechargeId(null);
    setRechargeAmount('');
  };

  // Stable requestId per active recharge popover so client-side re-renders during
  // submit reuse the same id and the server can dedupe via idempotency.
  const rechargeRequestIdRef = React.useRef<string | null>(null);

  const handleRecharge = (accountId: string) => {
    const amountNum = parseFloat(rechargeAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) return;
    if (!rechargeRequestIdRef.current) {
      rechargeRequestIdRef.current = crypto.randomUUID();
    }
    onRecharge?.(accountId, rechargeAmount.trim(), rechargeRequestIdRef.current);
    rechargeRequestIdRef.current = null;
    closeRecharge();
  };

  const inBatchMode = Boolean(batchMode);
  const selectedCount = selected?.size ?? 0;
  const overBatchLimit = selectedCount > MAX_BATCH_SIZE;
  const pageAccounts = data ?? [];
  const pageAllSelected =
    pageAccounts.length > 0 && pageAccounts.every((a) => selected?.has(a.accountId));
  const pageSomeSelected = !pageAllSelected && pageAccounts.some((a) => selected?.has(a.accountId));

  const columns: ColumnDef<AdsAccount>[] = [
    ...(inBatchMode
      ? [
          {
            id: 'select',
            meta: { headerClassName: 'w-[44px]', cellClassName: 'w-[44px]' },
            header: () => (
              <Checkbox
                aria-label="全选本页"
                // Checkbox 只内建了 checked 态的配色，indeterminate 在使用处补
                className="data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground"
                checked={pageAllSelected ? true : pageSomeSelected ? 'indeterminate' : false}
                onCheckedChange={(checked) => onTogglePage?.(pageAccounts, checked === true)}
              />
            ),
            cell: ({ row }) => (
              <Checkbox
                aria-label={`选择 ${row.original.accountCode}`}
                checked={selected?.has(row.original.accountId) ?? false}
                onCheckedChange={(checked) => onToggleSelect?.(row.original, checked === true)}
              />
            ),
          } satisfies ColumnDef<AdsAccount>,
        ]
      : []),
    {
      accessorKey: 'accountCode',
      header: '广告账户 ID',
      meta: { headerClassName: 'min-w-[150px]' },
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="group flex max-w-[200px] items-center gap-1.5">
                <Link
                  to="/ads/accounts/$id"
                  params={{ id: row.original.accountId }}
                  className="truncate font-mono text-sm font-bold text-primary hover:underline md:text-[0.95rem]"
                >
                  {row.original.accountCode}
                </Link>
                <CopyButton
                  value={row.original.accountCode}
                  onCopied={() => toast({ description: '账户ID已复制' })}
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">
              系统 ID: {row.original.accountId}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },

    {
      accessorKey: 'accountName',
      header: '账户名称',
      meta: { headerClassName: 'min-w-[140px]' },
      cell: ({ row }) => (
        <span
          className="max-w-[180px] truncate text-sm"
          title={row.original.accountName ?? undefined}
        >
          {row.original.accountName || '-'}
        </span>
      ),
    },

    {
      accessorKey: 'platformName',
      header: '广告平台',
      meta: { headerClassName: 'min-w-[100px]' },
      cell: ({ row }) => row.original.platformName || '-',
    },
    {
      accessorKey: 'providerName',
      header: '广告户商',
      meta: { headerClassName: 'min-w-[110px]' },
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-[13px] text-muted-foreground">
          {row.original.providerName || '-'}
        </span>
      ),
    },
    {
      id: 'operator',
      header: '负责人',
      meta: { headerClassName: 'min-w-[100px]' },
      cell: ({ row }) => {
        const name = row.original.assignedOperatorName;
        return name ? (
          <span className="whitespace-nowrap text-[13px] text-muted-foreground">{name}</span>
        ) : (
          <span className="text-[13px] text-muted-foreground/50">未分配</span>
        );
      },
    },
    {
      accessorKey: 'activeAppName',
      header: '当前 App',
      meta: { headerClassName: 'min-w-[140px]' },
      cell: ({ row }) => {
        const name = row.original.activeAppName;
        const pkg = row.original.activeAppPkg;
        const display = name || pkg;
        if (!display) return <span className="text-muted-foreground/50">-</span>;
        return (
          <span
            className="block max-w-[180px] truncate text-[13px] text-muted-foreground"
            title={display}
          >
            {display}
          </span>
        );
      },
    },
    {
      accessorKey: 'totalRecharge',
      header: '累计充值',
      meta: { headerClassName: 'text-right min-w-[100px]' },
      cell: ({ row }) => (
        <MoneyCell
          value={row.original.totalRecharge}
          className="w-full text-right text-sm font-medium text-success"
        />
      ),
    },
    {
      accessorKey: 'totalConsume',
      header: '累计消耗',
      meta: { headerClassName: 'text-right min-w-[100px]' },
      cell: ({ row }) => (
        <MoneyCell
          value={row.original.totalSpend ?? row.original.totalConsume ?? '0'}
          className="w-full text-right text-sm font-medium text-foreground/70"
        />
      ),
    },
    {
      accessorKey: 'currentBalance',
      header: '账户余额',
      meta: { headerClassName: 'text-right min-w-[100px]' },
      cell: ({ row }) => (
        <MoneyCell
          value={row.original.currentBalance}
          className={cn(
            'w-full text-right text-base font-bold tracking-tight',
            parseFloat(row.original.currentBalance) > 0 ? 'text-foreground' : 'text-destructive',
          )}
        />
      ),
    },
    {
      accessorKey: 'status',
      header: '状态',
      meta: { headerClassName: 'min-w-[100px]' },
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <StatusBadge status={row.original.status} />
          {row.original.activeBindingUnavailable && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                </TooltipTrigger>
                <TooltipContent className="text-xs">当前绑定 App 已下架</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'createTime',
      header: '创建时间',
      meta: { headerClassName: 'min-w-[140px]' },
      cell: ({ row }) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatDateTime(row.original.createTime)}
        </span>
      ),
    },
    {
      accessorKey: 'updateTime',
      header: '更新时间',
      meta: { headerClassName: 'min-w-[140px]' },
      cell: ({ row }) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatDateTime(row.original.updateTime)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      meta: { headerClassName: 'w-[164px]' },
      cell: ({ row }) => {
        const isRechargeOpen = openRechargeId === row.original.accountId;
        const hasBinding = row.original.activeAppPkg != null;
        const isActive = row.original.status === 0;
        return (
          <div className="flex items-center justify-end gap-0.5 pr-1">
            {canRecharge && onRecharge && (
              <TooltipProvider delayDuration={400}>
                <Tooltip>
                  <Popover
                    open={isRechargeOpen}
                    onOpenChange={(open) => {
                      if (open && !isActive) return;
                      if (open) {
                        setOpenRechargeId(row.original.accountId);
                      } else if (isRechargeOpen) {
                        closeRecharge();
                      }
                    }}
                  >
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label="充值"
                          className={cn(
                            'h-7 gap-1 px-2 text-xs',
                            isActive
                              ? 'text-primary'
                              : 'pointer-events-none text-muted-foreground/50',
                            isRechargeOpen && 'bg-primary/10',
                          )}
                        >
                          <Coins className="h-3.5 w-3.5" />
                          充值
                        </Button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <PopoverContent side="bottom" align="end" sideOffset={8} className="w-64 p-3">
                      <p className="text-xs font-bold text-muted-foreground">
                        账户 {row.original.accountCode}
                      </p>
                      <div className="mb-3 flex items-center gap-1 text-xs text-muted-foreground">
                        当前余额{' '}
                        <MoneyCell
                          value={row.original.currentBalance}
                          className="inline text-xs font-bold text-foreground"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex gap-1.5">
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="充值金额"
                            value={rechargeAmount}
                            onChange={(e) => setRechargeAmount(e.target.value)}
                            className="h-9 text-sm"
                            autoFocus
                          />
                          <span className="flex h-9 items-center text-xs text-muted-foreground">
                            USD
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {[500, 1000, 5000].map((v) => (
                            <button
                              key={v}
                              type="button"
                              onClick={() =>
                                setRechargeAmount((prev) => String((parseFloat(prev) || 0) + v))
                              }
                              className="h-7 rounded border border-muted bg-muted/30 px-2 text-xs font-medium hover:bg-muted"
                            >
                              +{v >= 1000 ? `${v / 1000}k` : v}
                            </button>
                          ))}
                        </div>
                        <div className="flex justify-end gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs md:text-sm"
                            onClick={closeRecharge}
                          >
                            取消
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 text-xs md:text-sm"
                            disabled={
                              !(
                                Number.isFinite(parseFloat(rechargeAmount)) &&
                                parseFloat(rechargeAmount) > 0
                              )
                            }
                            onClick={() => handleRecharge(row.original.accountId)}
                          >
                            确认充值
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <TooltipContent>{isActive ? '充值' : '账户已封/清零'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {canReportConsume && (
              <TooltipProvider delayDuration={400}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label="录入消耗"
                      className={cn(
                        'h-7 gap-1 px-2 text-xs',
                        hasBinding
                          ? 'text-amber-600'
                          : 'pointer-events-none text-muted-foreground/50',
                      )}
                      onClick={() => setConsumeDialogAccount(row.original)}
                    >
                      <TrendingUp className="h-3.5 w-3.5" />
                      录入消耗
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{hasBinding ? '录入消耗' : '未绑定 App'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* 低频操作收进 ⋯ 菜单 */}
            <Popover
              open={openMenuId === row.original.accountId}
              onOpenChange={(o) => setOpenMenuId(o ? row.original.accountId : null)}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="更多操作"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={6} className="w-44 p-1">
                {canAssign && onAssign && (
                  <RowMenuItem
                    onClick={() => {
                      setAssignDialogAccount(row.original);
                      setOpenMenuId(null);
                    }}
                  >
                    <UserPlus className="h-4 w-4 text-muted-foreground" />
                    分配投手
                  </RowMenuItem>
                )}
                {canManageBinding && (
                  <RowMenuItem
                    onClick={() => {
                      setBindingDialogAccount(row.original);
                      setOpenMenuId(null);
                    }}
                  >
                    {hasBinding ? (
                      <RefreshCw className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                    )}
                    {hasBinding ? '切换 App' : '绑定 App'}
                  </RowMenuItem>
                )}
                <RowMenuItem
                  onClick={() => {
                    onViewDetail(row.original.accountId);
                    setOpenMenuId(null);
                  }}
                >
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  查看详情
                </RowMenuItem>
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
  ];

  const statusOptions = [
    { label: '全部状态', value: '__all__' },
    { label: '正常', value: '0' },
    { label: '已封', value: '1' },
    { label: '已清零', value: '2' },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <FilterToolbar
        moreCount={moreFilterCount}
        primary={
          <>
            <SearchInput
              placeholder="搜索广告账户 ID..."
              value={filters.search ?? ''}
              onChange={(v) => onFilterChange({ ...filters, search: v || undefined })}
            />
            <Select
              value={filters.status ?? '__all__'}
              onValueChange={(v) =>
                onFilterChange({
                  ...filters,
                  status: v === '__all__' ? undefined : (v as '0' | '1' | '2'),
                })
              }
            >
              <SelectTrigger className="h-9 w-32 text-sm">
                <SelectValue placeholder="状态" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-sm">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
        actions={
          <>
            {canBan && onStartBatch && (
              <Button
                variant={batchMode === 'ban' ? 'default' : 'outline'}
                onClick={() => onStartBatch('ban')}
              >
                批量封禁
              </Button>
            )}
            {canZero && onStartBatch && (
              <Button
                variant={batchMode === 'zero' ? 'default' : 'outline'}
                onClick={() => onStartBatch('zero')}
              >
                批量清零
              </Button>
            )}
            {onBatchCreate && (
              <Button variant="outline" onClick={onBatchCreate} className="gap-1.5">
                <Plus className="h-4 w-4" />
                批量录入
              </Button>
            )}
            {onCreateAccount && (
              <Button onClick={onCreateAccount} className="gap-1.5 shadow-sm">
                <Plus className="h-4 w-4" />
                新建广告账户
              </Button>
            )}
          </>
        }
        more={
          <>
            <Input
              placeholder="账户名称..."
              value={filters.accountName ?? ''}
              onChange={(e) =>
                onFilterChange({ ...filters, accountName: e.target.value || undefined })
              }
              className="h-9 w-40 text-sm"
            />

            {filters.pkg && apps.length === 0 && (
              <div className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-muted/30 px-3 text-sm">
                <span className="text-muted-foreground">App</span>
                <span className="font-mono text-xs">{filters.pkg}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-5 w-5"
                  aria-label="清除 App 筛选"
                  onClick={() => onFilterChange({ ...filters, pkg: undefined })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}

            {apps.length > 0 && (
              <Select
                value={filters.pkg ?? '__all__'}
                onValueChange={(v) =>
                  onFilterChange({ ...filters, pkg: v === '__all__' ? undefined : v })
                }
              >
                <SelectTrigger className="h-9 w-44 text-sm">
                  <SelectValue placeholder="全部 App" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" className="text-sm">
                    全部 App
                  </SelectItem>
                  {apps.map((a) => (
                    <SelectItem key={a.pkg} value={a.pkg} className="text-sm">
                      {a.name || a.pkg}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select
              value={filters.platformId?.toString() ?? '__all__'}
              onValueChange={(v) =>
                onFilterChange({
                  ...filters,
                  platformId: v === '__all__' ? undefined : Number(v),
                })
              }
            >
              <SelectTrigger className="h-9 w-36 text-sm">
                <SelectValue placeholder="广告平台" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" className="text-sm">
                  全部平台
                </SelectItem>
                {platforms.map((p) => (
                  <SelectItem key={p.platformId} value={String(p.platformId)} className="text-sm">
                    {p.platformName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.providerId?.toString() ?? '__all__'}
              onValueChange={(v) =>
                onFilterChange({
                  ...filters,
                  providerId: v === '__all__' ? undefined : Number(v),
                })
              }
            >
              <SelectTrigger className="h-9 w-40 text-sm">
                <SelectValue placeholder="广告户商" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" className="text-sm">
                  全部户商
                </SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p.providerId} value={String(p.providerId)} className="text-sm">
                    {p.providerName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {canAssign && operators.length > 0 && (
              <Select
                value={filters.operatorId?.toString() ?? '__all__'}
                onValueChange={(v) =>
                  onFilterChange({
                    ...filters,
                    operatorId: v === '__all__' ? undefined : Number(v),
                  })
                }
              >
                <SelectTrigger className="h-9 w-40 text-sm">
                  <SelectValue placeholder="投手" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" className="text-sm">
                    全部投手
                  </SelectItem>
                  {operators.map((op) => (
                    <SelectItem key={op.userId} value={String(op.userId)} className="text-sm">
                      {op.displayName ? `${op.displayName} (${op.username})` : op.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <DateRangePicker
              label="创建时间"
              startValue={filters.createTimeStart}
              endValue={filters.createTimeEnd}
              onConfirm={(start, end) =>
                onFilterChange({ ...filters, createTimeStart: start, createTimeEnd: end })
              }
              onClear={() =>
                onFilterChange({ ...filters, createTimeStart: undefined, createTimeEnd: undefined })
              }
            />
            <DateRangePicker
              label="更新时间"
              startValue={filters.updateTimeStart}
              endValue={filters.updateTimeEnd}
              onConfirm={(start, end) =>
                onFilterChange({ ...filters, updateTimeStart: start, updateTimeEnd: end })
              }
              onClear={() =>
                onFilterChange({
                  ...filters,
                  updateTimeStart: undefined,
                  updateTimeEnd: undefined,
                })
              }
            />
          </>
        }
      />

      {inBatchMode && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold">
              {batchMode === 'ban' ? '批量封禁' : '批量清零'} · 已选 {selectedCount} 个账户
            </span>
            <span className="text-muted-foreground">勾选要处理的账户，完成后点右侧确认</span>
            {overBatchLimit && (
              <span className="text-destructive">最多 {MAX_BATCH_SIZE} 个，请先取消部分选择</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onCancelBatch?.()}>
              取消
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={selectedCount === 0 || overBatchLimit}
              onClick={() => onConfirmBatch?.()}
            >
              确认{batchMode === 'ban' ? '封禁' : '清零'}（{selectedCount}）
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={data || []}
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        pagination={{ page, pageSize, total, onPageChange }}
        emptyState={
          <EmptyState
            title="暂无账户数据"
            description="您可以点击下方按钮创建一个新的广告账户"
            action={
              onCreateAccount && (
                <Button variant="outline" onClick={onCreateAccount}>
                  新建广告账户
                </Button>
              )
            }
          />
        }
      />
      <ConsumeDialog account={consumeDialogAccount} onClose={() => setConsumeDialogAccount(null)} />
      <BindingDialog
        account={bindingDialogAccount}
        apps={apps}
        onClose={() => setBindingDialogAccount(null)}
      />
      <AssignDialog
        account={assignDialogAccount}
        operators={operators}
        onAssign={(accountId, operatorId) => onAssign?.(accountId, operatorId)}
        onClearAssign={onClearAssign ? (accountId) => onClearAssign(accountId) : undefined}
        onClose={() => setAssignDialogAccount(null)}
      />
    </div>
  );
}

function RowMenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
    >
      {children}
    </button>
  );
}

function AssignDialog({
  account,
  operators,
  onAssign,
  onClearAssign,
  onClose,
}: {
  account: AdsAccount | null;
  operators: OperatorUser[];
  onAssign: (accountId: string, operatorId: number) => void;
  onClearAssign?: (accountId: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = React.useState('');
  React.useEffect(() => {
    if (account) setSearch('');
  }, [account]);
  const filtered = operators.filter(
    (op) =>
      !search ||
      op.username.toLowerCase().includes(search.toLowerCase()) ||
      (op.displayName ?? '').toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <Dialog open={!!account} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>分配投手</DialogTitle>
          <DialogDescription>{account ? `账户 ${account.accountCode}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border border-input px-2.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索投手..."
            className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <ScrollArea className="h-72 rounded-md border">
          <div className="space-y-0.5 p-1">
            {filtered.map((op) => {
              const isCurrent = account?.assignedOperatorId === op.userId;
              return (
                <button
                  key={op.userId}
                  type="button"
                  onClick={() => {
                    if (account) onAssign(account.accountId, op.userId);
                    onClose();
                  }}
                  className={cn(
                    'flex w-full items-center justify-between rounded-sm px-2.5 py-2 text-sm transition-colors',
                    isCurrent ? 'bg-primary/10 text-primary' : 'hover:bg-accent',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback
                        className={cn('text-[10px] text-white', getAvatarColor(op.username))}
                      >
                        {op.username.slice(0, 1).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start">
                      <span className="font-medium leading-tight">
                        {op.displayName ?? op.username}
                      </span>
                      {op.displayName && (
                        <span className="text-[11px] text-muted-foreground">{op.username}</span>
                      )}
                    </div>
                  </div>
                  {isCurrent && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">无匹配投手</p>
            )}
          </div>
        </ScrollArea>
        <DialogFooter>
          {account?.assignedOperatorId != null && onClearAssign && (
            <Button
              variant="outline"
              className="mr-auto text-destructive hover:text-destructive"
              onClick={() => {
                onClearAssign(account.accountId);
                onClose();
              }}
            >
              清空分配
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
